import { useEffect, useState } from 'react';
import axios from 'axios';
import { HOME } from '@/constants/testIds';

const API = process.env.REACT_APP_BACKEND_URL;

const CommunitySubmitForm = ({ onClose }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [caption, setCaption] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploadedUrl, setUploadedUrl] = useState(null); // photo URL after upload
  const [uploading, setUploading] = useState(false);
  const [captioning, setCaptioning] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const onPickFile = async (f) => {
    setFile(f || null);
    setUploadedUrl(null);
    if (preview) URL.revokeObjectURL(preview);
    setPreview(f && f.type?.startsWith('image/') ? URL.createObjectURL(f) : null);
    // Auto-upload as soon as they pick — unlocks the "Caption me" button
    if (!f) return;
    setUploading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const up = await axios.post(`${API}/api/custom-requests/upload`, fd);
      setUploadedUrl(up.data.url);
    } catch (err) {
      setError('Photo upload failed. Try again.');
      setFile(null);
    } finally {
      setUploading(false);
    }
  };

  const askTeeodoreForCaption = async () => {
    if (!uploadedUrl || captioning) return;
    setCaptioning(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API}/api/community/caption-me`, {
        photo_url: uploadedUrl,
        hint: caption?.trim() || undefined,
      });
      if (data?.caption) setCaption(data.caption.slice(0, 400));
    } catch {
      setError('Teeodore is out on a coffee run — try again in a moment.');
    } finally {
      setCaptioning(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!file || !uploadedUrl) {
      setError('Pick a photo of you in your Shirt Happens gear.');
      return;
    }
    if (!name.trim() || !caption.trim()) {
      setError('Please tell us your name and add a quick caption.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/community/submit`, {
        name: name.trim(),
        email: email.trim() || undefined,
        caption: caption.trim(),
        photo_url: uploadedUrl,
        photo_filename: file.name,
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div
        data-testid="community-submit-success"
        className="p-8 rounded-3xl border-2 border-black bg-white shadow-[8px_8px_0_#FF6600] text-center"
      >
        <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full bg-brand-orange/15 border border-brand-orange/40">
          <span className="w-2 h-2 rounded-full bg-brand-orange animate-pulse" />
          <span className="font-display font-black text-[10px] tracking-[0.32em] uppercase text-brand-orange">
            Teeodore has entered the chat
          </span>
        </div>
        <div className="font-display font-black text-2xl mb-3 text-black">
          Thanks — you're in the queue!
        </div>
        <p className="font-body text-sm text-black/70 leading-relaxed mb-5">
          I'll review your submission and post it on the community wall as soon as I approve it. Usually within 24 hours.
        </p>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center gap-2 font-display font-bold text-xs uppercase tracking-[0.2em] px-5 py-3 rounded-full bg-black text-brand-orange border-2 border-black hover:bg-brand-orange hover:text-black transition-colors"
        >
          Close ←
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="community-submit-form"
      className="p-6 sm:p-8 rounded-3xl border-2 border-black bg-white shadow-[8px_8px_0_#FF6600]"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="font-display font-black text-2xl text-black">Post your shirt</div>
        <button type="button" onClick={onClose} className="text-black/50 hover:text-black text-2xl leading-none" aria-label="Close">×</button>
      </div>
      <p className="font-body text-sm text-black/60 mb-6">
        Rocking a Shirt Happens tee? Share it here — your photo may end up on the community wall.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <input
          data-testid="community-form-name"
          value={name} onChange={(e) => setName(e.target.value)} required maxLength={80}
          placeholder="Your name"
          className="bg-white border-2 border-black/20 focus:border-brand-orange outline-none rounded-xl px-4 py-3 font-body text-sm text-black"
        />
        <input
          type="email" data-testid="community-form-email"
          value={email} onChange={(e) => setEmail(e.target.value)} maxLength={200}
          placeholder="Email (optional — not shown publicly)"
          className="bg-white border-2 border-black/20 focus:border-brand-orange outline-none rounded-xl px-4 py-3 font-body text-sm text-black"
        />
      </div>
      <label
        htmlFor="community-photo"
        className={[
          'block rounded-2xl border-2 border-dashed cursor-pointer transition-colors mb-4',
          file ? 'border-brand-orange bg-orange-50/40' : 'border-black/20 bg-black/[0.02] hover:border-brand-orange hover:bg-orange-50/30',
          'px-5 py-5',
        ].join(' ')}
      >
        {file ? (
          <div className="flex items-center gap-4">
            {preview && <img src={preview} alt="preview" className="w-20 h-20 object-cover rounded-xl border border-black/10" />}
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-black truncate">{file.name}</div>
              <div className="text-xs text-black/50 font-body">
                {(file.size / 1024 / 1024).toFixed(1)} MB
                {uploading && <span className="ml-2 text-brand-orange">· uploading…</span>}
                {uploadedUrl && !uploading && <span className="ml-2 text-green-600">· ready ✓</span>}
              </div>
            </div>
            <button type="button" onClick={(e) => { e.preventDefault(); onPickFile(null); }} className="text-xs text-black/60 hover:text-red-600 uppercase tracking-[0.2em] font-display font-black">Remove</button>
          </div>
        ) : (
          <div className="flex items-center gap-4">
            <div className="text-3xl font-display font-black text-brand-orange leading-none">+</div>
            <div>
              <div className="font-display font-black text-black">Add your photo</div>
              <p className="font-body text-xs text-black/50 mt-1">JPG or PNG · up to 25 MB</p>
            </div>
          </div>
        )}
        <input id="community-photo" data-testid="community-form-photo" type="file" accept="image/*" onChange={(e) => onPickFile(e.target.files?.[0] || null)} className="hidden" />
      </label>
      <textarea
        data-testid="community-form-caption"
        value={caption} onChange={(e) => setCaption(e.target.value)} required maxLength={400} rows={3}
        placeholder="Say something! Where were you, what's the shirt, what happened?"
        className="w-full bg-white border-2 border-black/20 focus:border-brand-orange outline-none rounded-xl px-4 py-3 font-body text-sm text-black resize-none mb-2"
      />
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          data-testid="community-caption-me"
          onClick={askTeeodoreForCaption}
          disabled={!uploadedUrl || captioning}
          title={!uploadedUrl ? 'Attach a photo first — Teeodore needs to see it.' : 'Let Teeodore write it for you'}
          className={[
            'inline-flex items-center gap-2 rounded-full px-4 py-2 font-display font-black text-[10px] uppercase tracking-[0.22em] transition-all',
            !uploadedUrl
              ? 'bg-black/10 text-black/30 cursor-not-allowed'
              : captioning
                ? 'bg-black/60 text-white/60 cursor-wait'
                : 'bg-black text-brand-orange hover:bg-brand-orange hover:text-black border-2 border-black',
          ].join(' ')}
        >
          {captioning ? (
            <>
              <span className="w-3 h-3 rounded-full border-2 border-white/40 border-t-white animate-spin" />
              Teeodore is looking…
            </>
          ) : (
            <>🪄 Teeodore caption me</>
          )}
        </button>
        <div className="text-xs text-black/40 font-body">{caption.length} / 400</div>
      </div>
      {error && <div className="mb-4 p-3 rounded-xl bg-red-50 border-2 border-red-200 text-red-700 font-body text-sm">{error}</div>}
      <button
        type="submit" data-testid="community-form-submit" disabled={submitting}
        className={[
          'w-full font-display font-black text-sm uppercase tracking-[0.24em] py-4 rounded-full transition-all border-2 border-black',
          submitting ? 'bg-black/60 text-white/60 cursor-wait' : 'bg-brand-orange text-black hover:bg-black hover:text-brand-orange shadow-[4px_4px_0_#000]',
        ].join(' ')}
      >
        {submitting ? 'Uploading…' : 'Send it 🚀'}
      </button>
    </form>
  );
};

const CommunityCard = ({ post, index }) => (
  <div
    data-testid={`community-card-${index}`}
    className="break-inside-avoid mb-4 relative group rounded-2xl overflow-hidden bg-black border-2 border-white/10 hover:border-brand-orange transition-colors shadow-[6px_6px_0_rgba(255,102,0,0.15)] hover:shadow-[8px_8px_0_rgba(255,102,0,0.35)]"
  >
    <img
      src={`${API}${post.photo_url}`}
      alt={`${post.name} in Shirt Happens gear`}
      className="w-full block object-cover"
      loading="lazy"
    />
    <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black via-black/80 to-transparent">
      <div className="font-display font-black text-white text-base leading-tight">
        {post.name}
      </div>
      <p className="font-body text-xs text-white/85 leading-relaxed mt-1 line-clamp-3">
        {post.caption}
      </p>
    </div>
  </div>
);

const CommunityWallSection = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openSubmit, setOpenSubmit] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/community/wall`);
        if (!cancelled) setPosts(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setPosts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <section
      id="community"
      data-testid={HOME.communitySection}
      className="relative py-24 md:py-28 px-6 bg-black overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute top-24 left-1/2 -translate-x-1/2 w-[42rem] h-[42rem] rounded-full opacity-15 blur-3xl pointer-events-none"
        style={{ background: 'radial-gradient(circle, #FF6600 0%, transparent 60%)' }}
      />

      <div className="relative max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full bg-brand-orange/15 border border-brand-orange/40">
              <span className="w-2 h-2 rounded-full bg-brand-orange animate-pulse" />
              <span className="font-display font-black text-[11px] tracking-[0.32em] uppercase text-brand-orange">
                Community wall
              </span>
            </div>
            <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[0.95] text-white tracking-tight">
              Show your <span className="text-brand-orange">Shirt Happening.</span>
            </h2>
            <p className="font-body text-base sm:text-lg text-white/70 leading-relaxed mt-4 max-w-xl">
              Wearing your Shirt Happens tee somewhere fun? Send the photo &mdash; the best ones end up here for the world to see.
            </p>
          </div>
          <button
            type="button"
            data-testid="community-open-submit"
            onClick={() => setOpenSubmit(true)}
            className="self-start md:self-end inline-flex items-center gap-2 font-display font-black text-xs uppercase tracking-[0.24em] px-6 py-3.5 rounded-full bg-brand-orange text-black border-2 border-brand-orange hover:bg-white hover:border-white transition-colors shadow-[4px_4px_0_rgba(255,102,0,0.3)]"
          >
            + Post your shirt
          </button>
        </div>

        {loading ? (
          <div className="text-center text-white/40 font-body py-16">
            Loading the wall&hellip;
          </div>
        ) : posts.length === 0 ? (
          <div className="rounded-3xl border-2 border-dashed border-white/15 py-16 text-center">
            <div className="font-display font-black text-2xl text-white mb-3">Be the first to post.</div>
            <p className="font-body text-sm text-white/60 mb-6 max-w-md mx-auto">
              The wall goes live the moment the first Shirt Happens customer submits their pic. Might as well be you.
            </p>
            <button
              type="button"
              onClick={() => setOpenSubmit(true)}
              className="inline-flex items-center gap-2 font-display font-black text-xs uppercase tracking-[0.24em] px-5 py-3 rounded-full bg-brand-orange text-black hover:bg-white transition-colors"
            >
              + Post your shirt
            </button>
          </div>
        ) : (
          <div className="columns-1 sm:columns-2 md:columns-3 lg:columns-4 gap-4">
            {posts.map((post, i) => (
              <CommunityCard key={post.id} post={post} index={i} />
            ))}
          </div>
        )}

        {openSubmit && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in"
            onClick={(e) => { if (e.target === e.currentTarget) setOpenSubmit(false); }}
          >
            <div className="w-full max-w-lg max-h-[92vh] overflow-y-auto">
              <CommunitySubmitForm onClose={() => setOpenSubmit(false)} />
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default CommunityWallSection;
